import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { keycloak } from "@/utils/lib/keycloak";
import { useEffect, useRef } from "react";
import { Null } from "@/components/null";
import { useQueryClient } from "@tanstack/react-query";
import notificationSoundFile from "@/assets/notification.wav";
import { useUserNotificationPreference } from "@/api/notifications";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useProfileByKeycloakId } from "@/api/auth";

export function NotificationSocket() {
  // user profile
  const { id: keycloakId } = useCurrentUser();
  const { data } = useProfileByKeycloakId({ keycloakId: keycloakId });
  const userId = data?.id;

  const queryClient = useQueryClient();
  const notificationSound = useRef(new Audio(notificationSoundFile));
  notificationSound.current.preload = "auto";

  const { data: userPreference } = useUserNotificationPreference({
    userId: userId ?? "",
  });

  useEffect(() => {
    // 1. Inisialisasi Klien STOMP
    const stompClient = new Client({
      // Menggunakan SockJS sebagai webSocketFactory
      webSocketFactory: () => new SockJS(import.meta.env.VITE_API_BASE + "/ws"),

      // Kirim token via connectHeaders (Sangat aman dan rapi)
      connectHeaders: {
        Authorization: `Bearer ${keycloak.token}`,
      },

      reconnectDelay: 5000, // Otomatis reconnect setiap 5 detik jika putus
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    // 2. Logika saat berhasil terhubung (Connected)
    stompClient.onConnect = (_frame) => {
      // 3. Langganan (SUBSCRIBE) ke topik notifikasi user spesifik
      // Jalur '/user/topic/messages' ini biasanya ditentukan oleh Backend Spring Boot kamu
      stompClient.subscribe("/user/queue/notifications", (message) => {
        if (message.body) {
          console.info("Notification Received");

          if (userPreference?.isDoNotDisturb === false) {
            notificationSound.current.play().catch(() => {
              // Autoplay blocked by browser policy — user hasn't interacted yet
              console.info(
                "Autoplay blocked by browser policy — user hasn't interacted yet",
              );
            });
          }

          queryClient.invalidateQueries({
            queryKey: ["user-notification-preference"],
          });
          queryClient.invalidateQueries({
            queryKey: ["notifications"],
          });
          queryClient.invalidateQueries({
            queryKey: ["notifications-counter"],
          });
        }
      });
    };

    stompClient.onStompError = (frame) => {
      console.error("Broker reported error: " + frame.headers["message"]);
      console.error("Additional details: " + frame.body);
    };

    // 4. Aktifkan koneksi
    stompClient.activate();

    // 5. Cleanup saat komponen tidak digunakan (unmount)
    return () => {
      stompClient.deactivate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreference]);

  return <Null />;
}
